import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Award, Lock, CheckCircle, Target, 
  Zap, TrendingUp, Calendar, MessageSquare, Users
} from "lucide-react";

export const RapidCompletionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [finalAcknowledged, setFinalAcknowledged] = useState(false);

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.completion_data?.acknowledged) {
          setFinalAcknowledged(true);
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  const isLocked = () => {
    if (!enrollment) return true;
    return enrollment.status !== "completed";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="completion-locked">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Lock className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Completion Page is Locked</h1>
          <p className="text-neutral-600 mb-6">Your consultant will unlock this page once your R.A.P.I.D.™ engagement is complete.</p>
          <Button onClick={() => navigate("/rapid")} className="bg-emerald-600 hover:bg-emerald-700">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-completion">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-600 text-white">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/rapid")} 
            className="text-emerald-100 hover:text-white hover:bg-white/10 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-6">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2">R.A.P.I.D.™ Business Recalibration — Complete</h1>
            <Badge className="bg-white/20 text-white border-none text-sm px-3 py-1">
              Engagement Completed
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Completion Message */}
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-emerald-900 mb-4">
              Congratulations on completing your R.A.P.I.D.™ Business Recalibration.
            </h2>
            <p className="text-emerald-800 leading-relaxed">
              Your decisions are now aligned with your leadership capacity.<br />
              Execution will continue to strengthen through application—not overanalysis.
            </p>
          </CardContent>
        </Card>

        {/* Outcomes Summary */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-lg">Outcomes Summary</CardTitle>
            <CardDescription>Key results from your R.A.P.I.D.™ engagement</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {enrollment?.completion_data?.outcomes_summary ? (
              <div className="grid md:grid-cols-2 gap-6">
                {enrollment.completion_data.outcomes_summary.decisions_clarified && (
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Target className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-900">Decisions Clarified</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {enrollment.completion_data.outcomes_summary.decisions_clarified}
                      </p>
                    </div>
                  </div>
                )}
                {enrollment.completion_data.outcomes_summary.constraints_removed && (
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-900">Constraints Removed</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {enrollment.completion_data.outcomes_summary.constraints_removed}
                      </p>
                    </div>
                  </div>
                )}
                {enrollment.completion_data.outcomes_summary.standards_installed && (
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-900">Standards Installed</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {enrollment.completion_data.outcomes_summary.standards_installed}
                      </p>
                    </div>
                  </div>
                )}
                {enrollment.completion_data.outcomes_summary.momentum_activated && (
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-neutral-900">Momentum Activated</h4>
                      <p className="text-sm text-neutral-600 mt-1">
                        {enrollment.completion_data.outcomes_summary.momentum_activated}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900">Decisions Clarified</h4>
                    <p className="text-sm text-neutral-600 mt-1">
                      Key decision bottlenecks identified and resolved
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900">Constraints Removed</h4>
                    <p className="text-sm text-neutral-600 mt-1">
                      Internal constraints limiting execution addressed
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900">Standards Installed</h4>
                    <p className="text-sm text-neutral-600 mt-1">
                      New leadership standards and decision filters in place
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900">Momentum Activated</h4>
                    <p className="text-sm text-neutral-600 mt-1">
                      Execution rhythm and accountability framework established
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Next Steps CTAs */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-lg">Next-Step Options</CardTitle>
            <CardDescription>Continue your growth journey</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-1 gap-4">
              <Button 
                variant="outline" 
                className="h-auto p-4 justify-start"
                onClick={() => toast.info("Contact your consultant to apply for Ongoing Advisory")}
                data-testid="cta-advisory"
              >
                <Calendar className="w-5 h-5 mr-3 text-emerald-600" />
                <div className="text-left">
                  <p className="font-medium">Apply for Ongoing Advisory</p>
                  <p className="text-sm text-neutral-500">Continue with regular strategic support</p>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto p-4 justify-start"
                onClick={() => toast.info("Contact your consultant to schedule a Follow-Up Recalibration")}
                data-testid="cta-recalibration"
              >
                <MessageSquare className="w-5 h-5 mr-3 text-blue-600" />
                <div className="text-left">
                  <p className="font-medium">Schedule a Follow-Up Recalibration</p>
                  <p className="text-sm text-neutral-500">Check in after 90 days to assess progress</p>
                </div>
              </Button>
              
              <Button 
                variant="outline" 
                className="h-auto p-4 justify-start"
                onClick={() => toast.info("Contact your consultant to add Strategic Consulting Support")}
                data-testid="cta-strategic"
              >
                <Users className="w-5 h-5 mr-3 text-purple-600" />
                <div className="text-left">
                  <p className="font-medium">Add Strategic Consulting Support</p>
                  <p className="text-sm text-neutral-500">Expand to additional business areas</p>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Final Confirmation */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="final-acknowledgment"
                checked={finalAcknowledged}
                onCheckedChange={setFinalAcknowledged}
                className="mt-1"
                data-testid="final-acknowledgment"
              />
              <label htmlFor="final-acknowledgment" className="cursor-pointer">
                <p className="font-medium text-amber-900">Final Acknowledgment</p>
                <p className="text-sm text-amber-800 mt-1">
                  I acknowledge the completion of this engagement and commit to applying the decisions and standards established.
                </p>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => navigate("/rapid/momentum")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            View Momentum Plan
          </Button>
          <Button onClick={() => navigate("/rapid")} className="bg-emerald-600 hover:bg-emerald-700">
            Back to Dashboard
          </Button>
        </div>

        {/* Completion Date */}
        {enrollment?.completion_data?.completed_at && (
          <div className="text-center text-sm text-neutral-500 pt-4">
            Engagement completed on {new Date(enrollment.completion_data.completed_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RapidCompletionPage;
